'use strict';

import queryString from 'query-string';

/**
 * URL handling for the v2 invoice financing form. Same two URL shapes as the loan form:
 *   /hae-laskurahoitusta/invoicevolume=80000&paymentterms=30
 *   /hae-laskurahoitusta/?invoicevolume=80000&paymentterms=30
 * Both parameters present → "prefilled" mode (collapsed summary with an edit link).
 */

export const VOLUME = { min: 5000, max: 500000, step: 5000, default: 50000 }; // monthly invoicing to be financed
// 50 000 € sits where 160 000 € sat on the linear 5 000–500 000 track; the slider still reaches 500 000 €.
export const VOLUME_KNEE = { value: 50000, at: (160000 - VOLUME.min) / (VOLUME.max - VOLUME.min) };
// One slider stop per provision band. `days` is the value sent as paymentTerms.
export const TERM_BANDS = [
  { days: 14, label: '0-14' },
  { days: 30, label: '15-30' },
  { days: 45, label: '31-45' },
  { days: 90, label: 'yli 45' },
];
export const TERM_STOPS = TERM_BANDS.map((band) => band.days);
export const TERMS = { min: TERM_STOPS[0], max: TERM_STOPS[TERM_STOPS.length - 1], stops: TERM_STOPS, default: 45 };
export const formatTerms = (days) => (TERM_BANDS.find((band) => days <= band.days) || TERM_BANDS[TERM_BANDS.length - 1]).label;

const clamp = (n, { min, max }) => Math.min(max, Math.max(min, n));
const snap = (n, { step }) => Math.round(n / step) * step;
const snapToBand = (n) => (TERM_BANDS.find((band) => n <= band.days) || TERM_BANDS[TERM_BANDS.length - 1]).days;

export function parseInvoiceParams(match, search) {
  const pathParams = queryString.parse((match && match.params && match.params.id) || '');
  const query = queryString.parse(search || '');
  const values = { ...query, ...pathParams };

  const volume = parseInt(values.invoicevolume, 10);
  const terms = parseInt(values.paymentterms, 10);
  const prefilled = !isNaN(volume) && !isNaN(terms);

  return {
    values,
    mode: prefilled ? 'prefilled' : 'open',
    fin: {
      volume: prefilled ? snap(clamp(volume, VOLUME), VOLUME) : VOLUME.default,
      terms: prefilled ? snapToBand(clamp(terms, TERMS)) : TERMS.default,
    },
  };
}

/** Provision by payment term. No fixed euro fee on any band. */
const PROVISION_BANDS = [
  { maxDays: 14, percent: 0.85 },
  { maxDays: 30, percent: 1.75 },
  { maxDays: 45, percent: 2.5 },
  { maxDays: Infinity, percent: 3.45 },
];

export function feePercent(paymentTerms) {
  const band = PROVISION_BANDS.find((b) => paymentTerms <= b.maxDays);
  return (band || PROVISION_BANDS[PROVISION_BANDS.length - 1]).percent;
}

export const feeRate = (paymentTerms) => feePercent(paymentTerms) / 100;

export function calculateFactoring({ volume, terms }) {
  const ratePercent = feePercent(terms);
  const rate = ratePercent / 100;
  const monthlyFee = Math.round((volume * ratePercent) / 100);
  const rateLabel = ratePercent.toLocaleString('fi-FI', { maximumFractionDigits: 2 });
  return { rate, ratePercent, rateLabel, monthlyFee, immediateFunding: volume - monthlyFee };
}

export const BILLING_SYSTEMS = [
  { value: 0, label: 'En tiedä tai en ole varma' },
  { value: 1, label: 'Netvisor' },
  { value: 2, label: 'Procountor' },
  { value: 3, label: 'Fennoa' },
  { value: 4, label: 'Askelma' },
  { value: 5, label: 'Invoicer' },
];
